import { ShopCore } from "@/abilities/shop/types/shop-core";
import { ShopLight } from "@/abilities/shop/types/shop-light";
import {
    useGetArticleShopInfoLazyQuery,
    useGetArticleWithContextShopInfoLazyQuery,
    useGetDetailsForArticlesLazyQuery,
    useGetDetailsForArticlesWithContextLazyQuery,
} from "@/shared/services/graphql/generated/consumer-graph-types";
import { getDataQueryLanguages } from "@/shared/services/providers/language-provider";

const NETWORK_ONLY_FETCH_POLICY = {
    fetchPolicy: "network-only" as const,
};

export const useShopInfoApi = () => {
    /**
     * Fetches article information based on the article ID.
     * To be used in the shop widget
     * @param articleId - The ID of the article to fetch.
     * @returns A promise that resolves to the article information or null if not found.
     */
    const articleInfo = async (articleId: string): Promise<ShopCore.ItemInfo | null> => {
        const { load } = useGetArticleShopInfoLazyQuery({ articleId }, NETWORK_ONLY_FETCH_POLICY);
        const data = await load();
        if (data && data.article) {
            const { article } = data;
            return {
                price: article.price === null || isNaN(Number(article.price)) ? null : Number(article.price),
                currencySign: article.currency ?? "",
                stockStatus: article.orderable ? 0 : -1,
                amountAvailable: -1, // shop light doesn't support amountAvailable
                showPriceOnRequest: false,
            };
        }
        return null;
    };

    /**
     * Fetches mechanic information based on the mechanic ID.
     * To be used in the shop widget
     * @param mechanicId - The ID of the mechanic to fetch.
     * @returns A promise that resolves to the mechanic information or null if not found.
     */
    const mechanicInfo = async (mechanicId: string): Promise<ShopCore.ItemInfo | null> => {
        const { load } = useGetArticleWithContextShopInfoLazyQuery({ mechanicId }, NETWORK_ONLY_FETCH_POLICY);
        const data = await load();
        if (data && data.mechanic) {
            const { mechanic } = data;
            return {
                price: mechanic.price === null || isNaN(Number(mechanic.price)) ? null : Number(mechanic.price),
                currencySign: mechanic.currency ?? "",
                stockStatus: mechanic.orderable ? 0 : -1,
                amountAvailable: -1, // shop light doesn't support amountAvailable
                showPriceOnRequest: false,
            };
        }
        return null;
    };

    /**
     * Fetches detailed information for multiple articles.
     * To be used for enrichment in the shop basket list
     * @param articleIds - The IDs of the articles to fetch.
     * @returns A promise that resolves to an array of article enrichment information.
     */
    const detailsForArticles = async (articleIds: string[]): Promise<ShopLight.AdditionalArticleInfo[]> => {
        const { load } = useGetDetailsForArticlesLazyQuery(
            {
                filter: {
                    ids: articleIds,
                },
                acceptedLanguages: getDataQueryLanguages(),
            },
            NETWORK_ONLY_FETCH_POLICY
        );
        const data = await load();
        if (data && data.articles) {
            const articles = data.articles.articles.map((article) => article?.node);
            return articles.map((article) => ({
                id: article?.id ?? "",
                articleNumber: article?.articleNumber ?? "",
                description: article?.localizations?.title ?? "",
                imageUrl: article?.previewImage?.url ?? "",
                price: {
                    amount:
                        article?.price === null || isNaN(Number(article?.price)) ? undefined : Number(article?.price),
                    currency: article?.currency ?? "",
                },
                orderable: article?.orderable ? 0 : -1,
            }));
        }
        return [];
    };

    /**
     * Fetches detailed information for multiple articles with context.
     * To be used for enrichment in the shop basket list
     * @param mechanicIds - The IDs of the mechanics to fetch.
     * @returns A promise that resolves to an array of article enrichment information.
     */
    const detailsForArticlesWithContext = async (mechanicIds: string[]): Promise<ShopLight.AdditionalArticleInfo[]> => {
        const { load } = useGetDetailsForArticlesWithContextLazyQuery(
            {
                filter: {
                    ids: mechanicIds,
                },
                acceptedLanguages: getDataQueryLanguages(),
            },
            NETWORK_ONLY_FETCH_POLICY
        );
        const data = await load();
        if (data && data.mechanics) {
            const articlesWithContext = data.mechanics.mechanics.map((mechanic) => mechanic?.node);
            return articlesWithContext.map((article) => ({
                id: article?.id ?? "",
                articleNumber: article?.articleNumber ?? "",
                assemblyId: article?.assemblyId ?? undefined,
                description: article?.localizations?.title ?? "",
                imageUrl: article?.previewImage?.url ?? "",
                price: {
                    amount:
                        article?.price === null || isNaN(Number(article?.price)) ? undefined : Number(article?.price),
                    currency: article?.currency ?? "",
                },
                orderable: article?.orderable ? 0 : -1,
            }));
        }
        return [];
    };

    return {
        articleInfo,
        mechanicInfo,
        detailsForArticles,
        detailsForArticlesWithContext,
    };
};
